import React, { useEffect, useState } from 'react';
import {
  DeleteIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  TimeIcon,
} from '@chakra-ui/icons';
import { GetAiAssistantsQuery } from '../../../../generated/graphqlModels';
import {
  Avatar,
  AvatarGroup,
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Button,
} from '@chakra-ui/react';
import Tippy from '@tippyjs/react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../../hooks/isMobile';
interface HeaderProps {
  assistants: GetAiAssistantsQuery['aiAssistants'];
  selectedAssistant: GetAiAssistantsQuery['aiAssistants']['0'];
  setSelectedAssistantId: (value: string) => void;
  clearThread: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  threads?: Array<{ id: string; updatedAt: string; messages: any[] }>;
  onThreadSelect?: (threadId: string) => void;
  isBlock?: boolean;
}

const Header = (props: HeaderProps) => {
  const {
    assistants,
    selectedAssistant,
    setSelectedAssistantId,
    clearThread,
    isExpanded,
    onToggleExpand,
    threads,
    onThreadSelect,
    isBlock,
  } = props;

  const isMobile = useIsMobile();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { t } = useTranslation();

  const handleClearThread = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowConfirmationModal(true);
  };

  return (
    <div className="chat-header">
      <HStack role="group" _hover={{ cursor: 'pointer' }}>
        <Avatar
          size="md"
          name={selectedAssistant.name}
          src={selectedAssistant.imageUrl || undefined}
          zIndex={1}
        />
        <AvatarGroup _groupHover={{ display: 'flex' }} display="none" size="md">
          {assistants.map((assistant) => {
            if (assistant.id === selectedAssistant.id) {
              return null;
            }
            return (
              <Tippy
                key={assistant.id}
                arrow={true}
                theme="light"
                animation="scale"
                content={assistant.name}
              >
                <Avatar
                  _hover={{ border: '1px solid blue' }}
                  onClick={() => setSelectedAssistantId(assistant.id)}
                  size="md"
                  name={assistant.name}
                  src={(assistant as any).image?.url}
                  marginInlineStart="-4"
                />
              </Tippy>
            );
          })}
        </AvatarGroup>
      </HStack>
      <div className="chat-about">
        <div className="chat-with">{selectedAssistant.name}</div>
        <div className="chat-num-messages">{t('aiContainer.aiAssistant')}</div>
      </div>
      <div className="chat-toolbar">
        {!isBlock && (
          <IconButton
            icon={
              isMobile ? (
                <ArrowDownIcon />
              ) : isExpanded ? (
                <ArrowDownIcon />
              ) : (
                <ArrowUpIcon />
              )
            }
            onClick={onToggleExpand}
            aria-label={isExpanded ? 'Minimize chat' : 'Expand chat'}
            float="right"
            mr={2}
          />
        )}

        {threads && threads.length > 0 && (
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<TimeIcon />}
              aria-label="Previous chats"
              mr={2}
            />
            <MenuList maxH="300px" overflow="auto">
              {threads.map((thread) => (
                <MenuItem
                  key={thread.id}
                  onClick={() => onThreadSelect?.(thread.id)}
                >
                  <Box style={{ maxWidth: '200px' }}>
                    <Text fontSize="sm">
                      {new Date(thread.updatedAt).toLocaleDateString()}
                    </Text>
                    {thread?.messages &&
                      thread.messages[thread.messages.length - 1]?.content && (
                        <Text fontSize="xs" color="gray.600" noOfLines={1}>
                          {
                            thread?.messages[thread.messages.length - 1]
                              ?.content
                          }
                        </Text>
                      )}
                  </Box>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        )}
        <IconButton
          icon={<DeleteIcon />}
          onClick={handleClearThread}
          aria-label="Clear thread"
          float="right"
        />
      </div>

      {showConfirmationModal &&
        createPortal(
          <div className="confirmation-modal">
            <div className="confirmation-modal-content">
              <Text>
                <b>{t('aiContainer.areYouSureToClearThread')}</b>
              </Text>
              <hr />
              <Text>{t('aiContainer.clearThreadInfo')}</Text>
            </div>
            <div className="confirmation-modal-actions">
              <Button onClick={() => setShowConfirmationModal(false)}>
                {t('general.cancel')}
              </Button>
              <Button
                onClick={() => {
                  setShowConfirmationModal(false);
                  clearThread();
                }}
              >
                {t('general.clear')}
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Header;
